import type { TypeTableColumn } from '@thefarmersfront/kitchen';

import { formatNumber } from '~/utils/common';
import { formatDate } from '~/utils/date';

import type { TypeFailItem } from '~/types/receive.types';

const dateColumn = (
  accessorKey: 'manufactureDate' | 'expirationDate',
  header: string,
  size: number
): TypeTableColumn<TypeFailItem> => ({
  accessorKey,
  header,
  size,
  cell: ({ getValue }) => formatDate(getValue<string>(), 'YYYY.MM.DD') ?? '-',
});

type TypeFailItemQty = TypeFailItem['boxQty'];

// isNaN 은 ''·'1e3'·'0x10' 도 숫자로 보아 입력과 다른 값을 보여주므로 십진수 표기만 숫자로 본다
const DECIMAL_TEXT_PATTERN = /^-?\d+(\.\d+)?$/;

const formatQtyNumber = (value: number) => (value ? formatNumber(value) : '-');

const formatFailItemQty = (value: TypeFailItemQty | null | undefined) => {
  // formatNumber 는 number 가 아니면 throw 하므로, 응답이 타입과 달라도 number 만 넘긴다
  if (typeof value === 'number') return formatQtyNumber(value);
  if (typeof value !== 'string') return '-';

  const text = value.trim();
  if (!text) return '-';
  return DECIMAL_TEXT_PATTERN.test(text) ? formatQtyNumber(Number(text)) : text;
};

const countColumn = (
  accessorKey: 'boxQty' | 'boxPerQty' | 'eachQty',
  header: string
): TypeTableColumn<TypeFailItem> => ({
  accessorKey,
  header,
  size: 100,
  cell: ({ getValue }) => formatFailItemQty(getValue<TypeFailItemQty | null>()),
});

/**
 * [입고관리 > 입고요청관리] 입고요청등록 모달 > 엑셀 추가 실패 모달 테이블
 */
const getReceiveExcelUploadFailModalColumnDefs = ({ hasPermissionNFA }: { hasPermissionNFA: boolean }) => {
  const columnDefs: TypeTableColumn<TypeFailItem>[] = [
    {
      id: 'no',
      header: 'No',
      cell: ({ row }) => row.index + 1,
      size: 72,
    },
    {
      accessorKey: 'goodsCode',
      header: '상품코드',
      size: 140,
    },
    ...(hasPermissionNFA
      ? [{ accessorKey: 'nsid', header: 'NSID', size: 180 } satisfies TypeTableColumn<TypeFailItem>]
      : []),
    dateColumn('manufactureDate', '제조일자', 100),
    dateColumn('expirationDate', '유통/소비기한', 110),
    {
      accessorKey: 'palletYn',
      header: '파렛트 사용여부',
      size: 120,
    },
    countColumn('boxQty', '수량(BOX)'),
    countColumn('boxPerQty', '입수(BOX당)'),
    countColumn('eachQty', '수량(EA)'),
    {
      accessorKey: 'failReason',
      header: '실패사유',
      minSize: 700,
      meta: { flex: true },
    },
  ];

  return { columnDefs };
};

export default getReceiveExcelUploadFailModalColumnDefs;
